import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';

function formatCurrency(amount, symbol = 'R') {
  return `${symbol}${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function RecordPaymentDialog({ open, onOpenChange, invoice, onSubmit }) {
  const [form, setForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  if (!invoice) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        invoice_id: invoice.id,
        amount: parseFloat(form.amount) || 0,
        payment_date: form.payment_date,
        payment_method: form.payment_method,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
      });
      setForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'bank_transfer', reference: '', notes: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-wider">Record Payment</DialogTitle>
        </DialogHeader>

        <div className="bg-slate-50 border rounded-lg p-3 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Invoice</span>
            <span className="font-mono font-medium">{invoice.invoice_number}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-500">Total</span>
            <span className="font-medium">{formatCurrency(invoice.total)}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-slate-500">Already Paid</span>
            <span className="font-medium text-emerald-600">{formatCurrency(invoice.amount_paid)}</span>
          </div>
          <div className="flex justify-between text-sm font-bold mt-2 pt-2 border-t">
            <span>Balance Due</span>
            <span className="text-red-600">{formatCurrency(invoice.balance_due)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Amount (R) <span className="text-red-500">*</span></Label>
            <Input 
              type="number" step="0.01" min="0.01" 
              value={form.amount} 
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} 
              placeholder={`Max: ${(invoice.balance_due || 0).toFixed(2)}`}
              data-testid="payment-amount"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Payment Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} data-testid="payment-date" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Payment Method</Label>
            <Select value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer / EFT</SelectItem>
                <SelectItem value="card">Card Payment</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Payment Reference</Label>
            <Input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="EFT reference, cheque number, etc." data-testid="payment-reference" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Optional notes" data-testid="payment-notes" />
          </div>
          <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!form.amount || parseFloat(form.amount) <= 0 || loading} data-testid="submit-payment-btn">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : `Record Payment of ${formatCurrency(parseFloat(form.amount) || 0)}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RecordPaymentDialog;
